/*
   The Inbox nav badge and the unread preview under it (window.PortalInbox).

   A classic script in the core chain (CORE_SCRIPTS in lib/layout.js), enqueued straight after
   core.js, which it reads for t(), tCount() and showToast(), and before fetch-wrapper.js, which is
   the order the two held at the end of core.js. The browser extension's toolbar popup IS this
   panel, so extension/build.js takes this file with the rest of the core chain. Its history up to
   2026.09.11.02 is in core.js's version header.
*/

// ── Inbox badge + preview ─────────────────────────────────────────────────────────────────────
// Runs on every page EXCEPT the inbox itself (which manages its own state).
//
// THE NUMBER AND THE ROWS COME FROM ONE ANSWER: GET /api/inbox/unread (lib/inboxUnread.js), which
// the browser extension's toolbar reads too. This file used to add the number up itself — five
// per-source counters, two of them measured against a timestamp in this tab's storage — while the
// toolbar added up the three sources it could reach with no page, and the two disagreed (reported
// on 2026-09-12 as a header reading 4 beside a toolbar reading 1). Nothing here sums a count now:
// the badge shows `total` as the server sent it, and check:inboxpreview fails the deploy if it
// ever shows anything else.
//
// BroadcastChannel and SSE nudges make it ask again sooner; what the badge is showing is persisted
// so a refresh or a navigation doesn't blank it (saveBadgeState). Exposes window.PortalInbox for
// chatheads' SSE handler.
var INBOX_UNREAD_URL = '/api/inbox/unread';
var INBOX_SEEN_URL = '/api/inbox/seen';
// The "last opened the Inbox" timestamp this file used to keep. The server holds it now; a value
// still sitting here is handed over once (handOverBaseline) and removed.
var INBOX_LAST_TS_KEY = 'portal_inbox_last_ts';
// The badge's last painted state, so the indicator survives a refresh or a navigation.
var INBOX_BADGE_KEY = 'portal_inbox_badge';
// Older than this and the stamp is from another sitting entirely: the poll answers within
// seconds, so paint nothing rather than a count from days ago.
var INBOX_BADGE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
(function initInboxPoll() {
  /**
   * Is this the Inbox page? Two URLs render it — `/?mode=inbox` (what the nav links to) and the
   * bare `/inbox` path (the preview's "View all" footer, and every unknown mode, which the server
   * falls back to). This used to test `pathname === '/inbox'` alone, so it missed the URL people
   * actually arrive on and the "runs everywhere EXCEPT the inbox" poll ran on the Inbox itself.
   * The body class is the server's own answer (`bodyClass: mode-${modeKey}` in server.js), so it
   * covers all three cases at once.
   */
  function onInboxPage() {
    return !!(document.body && document.body.classList.contains('mode-inbox'));
  }
  /**
   * The badge's saved record — `{ total, counts, items, labels }`, the last three keyed by
   * itemType — or null when there is none, it doesn't parse, or it is older than
   * INBOX_BADGE_MAX_AGE_MS. One reader for both callers: the badge restoring its paint, and the
   * Inbox taking over the rows the badge found.
   *
   * Coerced on the way in rather than trusted: this is parsed storage, and a count that isn't a
   * number would reach the badge and a row list that isn't an array would break the preview.
   */
  function readBadgeRecord() {
    var raw = null;
    try { raw = JSON.parse(localStorage.getItem(INBOX_BADGE_KEY) || 'null'); } catch (_) { return null; }
    if (!raw || typeof raw !== 'object') return null;
    if (!raw.at || (Date.now() - raw.at) > INBOX_BADGE_MAX_AGE_MS) return null;
    var out = { total: count(raw.total), counts: {}, items: {}, labels: {}, nextAt: count(raw.nextAt), backoff: raw.backoff === true };
    if (raw.counts && typeof raw.counts === 'object') {
      Object.keys(raw.counts).forEach(function (type) { out.counts[type] = count(raw.counts[type]); });
    }
    if (raw.items && typeof raw.items === 'object') {
      Object.keys(raw.items).forEach(function (type) {
        if (Array.isArray(raw.items[type])) out.items[type] = raw.items[type];
      });
    }
    if (raw.labels && typeof raw.labels === 'object') {
      Object.keys(raw.labels).forEach(function (type) {
        if (typeof raw.labels[type] === 'string') out.labels[type] = raw.labels[type];
      });
    }
    return out;
  }
  /** A non-negative whole number, whatever arrived. */
  function count(v) {
    var n = Math.floor(Number(v));
    return (isFinite(n) && n > 0) ? n : 0;
  }

  if (onInboxPage()) {
    // Reading the Inbox is what clears the indicator, so drop the saved badge state on the way
    // in — otherwise the next page would paint the count this visit just settled. Anything still
    // genuinely unread comes back on that page's first poll, which asks the server, not this cache.
    //
    // The ROWS it found are handed to the Inbox first. The badge polls on every other page and the
    // Inbox paints its own snapshot, last written the last time the Inbox was open — so without
    // this, a message the preview had just shown was missing from the list until the Inbox's own
    // fetch came back seconds later. Which rows are list-shaped is each source's answer
    // (`acceptsPreviewRows` on its Inbox registration), not this file's.
    //
    // Deliberately NOT window.PortalInbox: chatheads.js reads that object's absence as "this is the
    // Inbox page" and stops nudging a badge that isn't there.
    var handoff = readBadgeRecord();
    try { localStorage.removeItem(INBOX_BADGE_KEY); } catch (_) {}
    window.PortalInboxHandoff = {
      /** The badge's rows keyed by itemType, once; null after the first call or when there are none. */
      take: function () { var h = handoff; handoff = null; return h ? h.items : null; }
    };
    return;
  }

  // The server's last answer. `_total` is the badge; the rest is its breakdown, keyed by itemType,
  // for the panel: each source's share, its newest rows, and the label key that names it.
  var _total = 0;
  var _counts = {};
  var _items  = {};
  var _labels = {};
  var _previewOpen = false;
  /**
   * Bumped by every LOCAL change to the rows — a mark-as-read, a delete, its undo.
   *
   * A poll is several hundred ms of flight, and the panel is opened by a hover that starts one.
   * So the row a reader marks read is very often marked while the request that will describe it
   * as unread is already on its way back, and applying that answer puts the row back. checkNew()
   * reads this before the request goes out and drops an answer that was overtaken; the answer to
   * the request made AFTER the action is the one that lands.
   */
  var _mutations = 0;

  /**
   * When to ask again, as the server said (`nextPollMs`, lib/inboxUnread.js). A reader with a
   * backlog is asked twice an hour or less, and while `_backoff` holds, nudges (a new message,
   * another tab, the window regaining focus) wait for it too; what the reader does in the panel
   * never waits. Saved with the paint, so a backed-off reader is not re-asked on every page load.
   */
  var POLL_BASE_MS = 90000;
  var _nextAt = 0;
  var _backoff = false;
  var _pollTimer = null;
  function schedule(ms) {
    if (_pollTimer) clearTimeout(_pollTimer);
    _pollTimer = setTimeout(function () { _pollTimer = null; checkNew(); }, Math.max(1000, ms));
  }
  /** Ask again because something MIGHT have changed — unless the server said this reader can wait. */
  function nudge(fresh) {
    if (_backoff && Date.now() < _nextAt) return;
    checkNew(fresh);
  }

  /** A source's preview rows, created on first use. */
  function itemsFor(type) { return _items[type] || (_items[type] = []); }

  /** The badge's number: the server's `total`, never a sum made here. */
  function total() { return _total; }

  /**
   * The name of the one source with unread, when exactly one has any — otherwise ''.
   *
   * total() alone would have the panel say "1 unread message", which is true and unhelpful: the
   * reader is looking at a badge that already told them the number. What they do not know is
   * WHERE, and the answer carries each source's share, so it is knowable. Only when ONE source has
   * any: two would need a list, and a list needs an order and a conjunction in three languages.
   *
   * The name is the source's registered label, translated, so this names no provider. A label
   * that does not resolve falls back to the generic line rather than printing a raw key.
   *
   * @param {Object} [counts]  itemType -> count; defaults to each source's share of the total
   */
  function pendingSource(counts) {
    var by = counts || _counts;
    var withUnread = Object.keys(by).filter(function (type) { return (by[type] || 0) > 0; });
    if (withUnread.length !== 1) return '';
    var key = _labels[withUnread[0]];
    if (!key) return '';
    var label = t(key);
    return label === key ? '' : label;
  }

  /**
   * Persist / restore what the badge is showing.
   *
   * Without this every full page load blanked the indicator until the first poll came back, which
   * on a site that navigates by full page loads reads as "unread messages vanish when you move
   * around". Only the PAINT is stored, never a shred of read state: the count is the server's.
   *
   * Bounded by construction: one record, at most one count and 5 rows per source, replaced on
   * write. A failed write (quota, private mode) leaves the badge blank until the poll answers.
   */
  function saveBadgeState() {
    try {
      localStorage.setItem(INBOX_BADGE_KEY, JSON.stringify({ total: _total, counts: _counts, items: _items, labels: _labels,
        nextAt: _nextAt, backoff: _backoff, at: Date.now() }));
    } catch (_) {}
  }
  function loadBadgeState() {
    var rec = readBadgeRecord();
    if (!rec) return;
    _total = rec.total; _counts = rec.counts; _items = rec.items; _labels = rec.labels;
    _nextAt = rec.nextAt; _backoff = rec.backoff;
  }

  /** Take the server's answer as it is. */
  function applyAnswer(d) {
    _total = count(d.total);
    _counts = {}; _items = {}; _labels = {};
    (Array.isArray(d.sources) ? d.sources : []).forEach(function (s) {
      if (!s || !s.itemType) return;
      _counts[s.itemType] = count(s.count);
      _items[s.itemType] = Array.isArray(s.items) ? s.items : [];
      if (s.labelKey) _labels[s.itemType] = String(s.labelKey);
    });
  }

  function updateBadge() {
    // Saved before the element check: the embed and onboarding shells run this script without a
    // nav, and the count they hold is still the right thing to hand the next full-shell page.
    saveBadgeState();
    var b = document.getElementById('inbox-count-badge');
    if (!b) return;
    var n = total();
    var link = document.getElementById('inbox-nav-link');
    if (n > 0) {
      // The exact number up to 99, then "99+" — the same as the browser extension's toolbar icon
      // (extension/src/background.js paintBadge). This clamped at "9+" while the icon beside it
      // showed 13, which read as the two disagreeing when they held the same count.
      b.textContent = n > 99 ? '99+' : String(n);
      b.style.display = 'inline-flex';
      // The badge is aria-hidden, so the real count rides on the link's own name. Uses the
      // true count, not the clamped "99+", because "99+ unread" is not a thing to announce.
      if (link) link.setAttribute('aria-label', tCount('inbox.nav_unread', n));
    } else {
      b.style.display = 'none';
      // Drop the label entirely rather than announcing "0 unread": with none set, the link
      // falls back to its own visible text, which is the correct name at zero.
      if (link) link.removeAttribute('aria-label');
      if (_previewOpen) closePreview();
    }
  }

  // Resolve a preview row's body text to plain, human-readable text before esc().
  // Slack rows run through the full reference resolver (users, channels, @here,
  // <!date^…>, links, emoji); other sources get emoji-only resolution so subjects
  // aren't mangled. Both resolvers live in slack-utils.js; guards cover the embed/
  // onboarding shells that don't load it (degrades to raw text, as before).
  function previewText(it) {
    var s = it.text || '';
    if (it.source === 'Slack' && typeof window.slackTextToPreview === 'function') {
      return window.slackTextToPreview(s, function(uid) {
        return window.SlackDirect && window.SlackDirect.getUser(uid);
      });
    }
    if (typeof window.resolveEmojiShortcodes === 'function') return window.resolveEmojiShortcodes(s);
    return s;
  }

  // Merge the per-source rows into one time-sorted list for the preview.
  function normItems() {
    var all = [];
    // A Slack row carrying `source` is a team channel row; the loop below takes it, so this
    // branch leaves it alone rather than listing it twice.
    itemsFor('slack').forEach(function(it) { if (it.source) return; all.push({ source: 'Slack', key: 'slack', id: it.id, rawTs: it.rawTs, name: it.name, text: it.text, avatar: it.avatar, ts: it.ts || 0 }); });
    itemsFor('gmail').forEach(function(it) { all.push({ source: 'Gmail', key: 'gmail', id: it.id, name: it.name, text: it.subject || it.snippet || '', avatar: it.avatar, ts: it.timestamp || 0 }); });
    itemsFor('google-chat').forEach(function(it) { all.push({ source: 'Chat', key: 'google-chat', id: it.id, name: it.name, text: it.snippet || '', avatar: it.avatar, ts: it.timestamp || 0 }); });
    // ── AND EVERY OTHER SOURCE, without a branch of its own ──────────────────────────────────
    // The three above are hand-mapped because each source sends the rows its own Inbox list uses
    // (the Inbox takes them over — see PortalInboxHandoff). Any other row arrives ALREADY in this
    // shape, carrying its own label and its own addresses — how to open it, and how to clear it —
    // so another source costs no branch here, in openInInbox or in markRead.
    //
    // `it.source` is what marks a row as arriving that way, and it is a structural test rather
    // than a list of names: the three mapped above carry no `source` until the lines above add one.
    Object.keys(_items).forEach(function (type) {
      itemsFor(type).forEach(function (it) {
        if (!it.source) return;
        all.push({ source: it.source, key: type, id: it.id, name: it.name, text: it.text,
          avatar: it.avatar, ts: it.ts || 0,
          open: it.open, readUrl: it.readUrl, readBody: it.readBody });
      });
    });
    all.sort(function(a, b) { return b.ts - a.ts; });
    return all.slice(0, 6);
  }

  // This card's own vocabulary for a source, translated to the one the link table speaks.
  var INBOX_PLATFORM = { Slack: 'slack', Chat: 'google-chat', Gmail: 'gmail' };

  // Navigate to the Inbox with the right deep-link so the conversation opens.
  //
  // Built by window.PortalSearchOpen, which owns every item -> destination in the app. This was a
  // hand-written third copy of that scheme, so a conversation opened one way from here, another
  // from a search result and another from a saved item.
  function openInInbox(it) {
    if (!it) return;
    // A row that arrived naming its own destination goes there. Such a row carries the item
    // PortalSearchOpen already knows how to address, so this needs no branch naming its source.
    //
    // Root-relative only. The value came from our own route through the canonical builder, but
    // this is a navigation sink and a sink is guarded where it is written, not upstream — and
    // url() answers '#' for an item it cannot address, which this refuses along with the rest.
    if (it.open) {
      var dest = (window.PortalSearchOpen && window.PortalSearchOpen.url(it.open)) || '';
      if (dest.charAt(0) === '/' && dest.charAt(1) !== '/') _go(dest, it);
      return;
    }
    var platform = (it.id && window.PortalSearchOpen) ? INBOX_PLATFORM[it.source] : '';
    _go(platform
      ? window.PortalSearchOpen.url({
        platform: platform,
        channelType: 'dm',
        channelId: it.id,
        // Only Slack items carry one; the others yield a conversation-level link.
        messageTs: it.rawTs || '',
        threadId: it.id,
      })
      : '/?mode=inbox', it);
  }

  // WHERE "open this" GOES. On a portal page it is this tab, which is what it has always been.
  //
  // The browser extension's toolbar popup draws this same panel, and there `location.href` means
  // the popup itself — a 380px window that would navigate to the Inbox and then close, having
  // shown it to nobody. A surface that is not a page takes the destination instead.
  //
  // The ITEM goes with the address: the extension opens the conversation as a panel on the tab the
  // reader is already looking at (Chatheads.openConversation), which needs the row, not a URL.
  var _opener = null;
  function _go(dest, it) {
    if (_opener) _opener(dest, it);
    else window.location.href = dest;
  }

  function postJson(url, body) {
    return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
  }

  /**
   * Take a row off the panel the moment the reader acts on it.
   *
   * The number moves down with it, as a GUESS shown only until the answer to the request made
   * after the action lands — that answer is the same question asked of the same server, and it
   * replaces this outright. It exists so the line under the rows doesn't flash "1 more unread"
   * for the row that was just cleared.
   */
  function dropRow(it) {
    if (it.key && _items[it.key]) {
      var before = _items[it.key].length;
      _items[it.key] = _items[it.key].filter(function (x) { return x.id !== it.id; });
      if (_items[it.key].length < before) {
        _counts[it.key] = Math.max(0, (_counts[it.key] || 0) - 1);
        _total = Math.max(0, _total - 1);
      }
    }
    _mutations++;
    updateBadge();
    renderPreview();
  }

  // Mark a Slack DM / Chat space / carried row as read. The server call persists the read state,
  // and the recount after it (that one source, past its cache) is what the badge then shows.
  function markRead(it) {
    if (!it || !it.id) return;
    var url, body;
    // A source that arrived with its own clear-address is cleared through it, with no branch
    // naming it. Guarded like every sink, and first, because the hand-mapped sources carry none.
    if (it.readUrl && it.readUrl.charAt(0) === '/' && it.readUrl.charAt(1) !== '/') {
      url = it.readUrl; body = it.readBody || {};
    } else if (it.source === 'Slack') {
      url = '/api/slack/cache-read'; body = { channelId: it.id };
      // Best-effort: also mark it read natively in Slack (conversations.mark).
      if (it.rawTs) postJson('/api/groups/mark-read', { id: it.id, ts: it.rawTs });
    } else if (it.source === 'Chat') {
      // The space's read marker, which the unread count reads for Chat.
      url = '/api/groups/unread'; body = { spaceId: it.id };
    } else return;
    dropRow(it);
    postJson(url, body).then(function () { checkNew(it.key); });
  }

  // Gmail delete, mirroring the Inbox: hide the row and trash immediately, then a 5s toast counts
  // down with an Undo that reverses it in Gmail via /untrash. If the window lapses, the trash
  // stands (the Inbox does the same — deferring the request would drop it if the tab closes).
  function gmailDelete(it) {
    if (!it || !it.id) return;
    var threadId = it.id;
    dropRow(it);

    var undo = null;
    function dropUndo() { if (undo) { undo.close(); undo = null; } }

    postJson('/api/gmail/trash', { threadId: threadId }).then(function (res) {
      if (!res || res.ok === false) {
        dropUndo();
        if (typeof window.showToast === 'function') window.showToast(window.t('inbox.msg_delete_failed'));
      }
      checkNew(it.key);
    });

    undo = window.showToast(window.t('inbox.msg_deleted'), {
      countdown: true,
      duration: 5000,
      action: {
        label: window.t('common.undo'),
        onAction: function () {
          postJson('/api/gmail/untrash', { threadId: threadId }).then(function (res) {
            if (!res || res.ok === false) window.showToast(window.t('inbox.msg_undo_gmail'));
            checkNew(it.key);
          });
        },
      },
    });
  }

  function renderPreview() {
    var el = document.getElementById('inbox-preview');
    if (!el) return;
    var items = normItems();

    // NEVER claim the reader is caught up while the badge beside this panel says otherwise.
    //
    // The badge is the server's total; these rows are the newest few of it. They can be fewer:
    // the assistant's pending plans are a count with no rows by design, the panel shows 6 at most,
    // and a restored paint may hold a count from before its rows. So the empty state answers the
    // BADGE rather than the list: at zero it says caught up, above zero it says how many are
    // waiting, and the footer below is the way to them.
    var pending = total();
    var only = pendingSource();
    var emptyText = pending <= 0 ? t('inbox.preview_caught_up')
      : only ? tCount('inbox.preview_pending_source', pending, { source: only })
        : tCount('inbox.preview_pending', pending);

    // …and the same rule holds when the list is NOT empty: whatever the answer counts that these
    // rows don't show is named under them, per source, so a partial list never reads as complete.
    var listed = {};
    items.forEach(function (it) { listed[it.key] = (listed[it.key] || 0) + 1; });
    var unlisted = {};
    var more = 0;
    Object.keys(_counts).forEach(function (type) {
      var n = Math.max(0, (_counts[type] || 0) - (listed[type] || 0));
      unlisted[type] = n;
      more += n;
    });
    var moreSource = pendingSource(unlisted);
    var moreText = more <= 0 ? ''
      : moreSource ? tCount('inbox.preview_more_source', more, { source: moreSource })
        : tCount('inbox.preview_more', more);

    // The markup is the theme's (themes/default/components/inboxPreview.js), because the browser
    // extension's toolbar popup is this same panel and "the same panel" has to mean the same file.
    // The message text is resolved here rather than there: previewText reaches for the Slack
    // renderer and the emoji table, which are this page's, not a theme's.
    el.textContent = '';
    el.appendChild(window.PortalH.toDom(window.PortalInboxPreviewTheme.panel({
      items: items.map(function (it) {
        return { source: it.source, name: it.name || t('inbox.preview_unknown'), text: previewText(it), avatar: it.avatar };
      }),
      emptyText: emptyText,
      moreText: moreText,
      headText: t('inbox.preview_unread_messages'),
      headAction: _headAction ? _headAction() : null,
      footText: t('inbox.preview_view_all'),
      inboxUrl: '/inbox',
      labels: {
        open: t('inbox.preview_open_in_inbox'),
        read: t('inbox.preview_mark_read'),
        del: t('inbox.preview_delete'),
      },
    }, window.PortalAvatars ? window.PortalAvatars.initials : null), document));

    // Apply each face over its initials, but only if it loads. See PortalAvatars.repair: these
    // rows carry an avatar captured when the conversation was cached, so a person who has changed
    // their photo since has a URL in here that no longer resolves.
    if (window.PortalAvatars && window.PortalAvatars.repair) window.PortalAvatars.repair(el);

    // Wire handlers against the closured items array.
    function idx(node) { return parseInt(node.getAttribute('data-i'), 10); }
    Array.prototype.forEach.call(el.querySelectorAll('.ip-open'), function(o) {
      o.addEventListener('click', function() { openInInbox(items[idx(o)]); });
      o.addEventListener('keydown', function(e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openInInbox(items[idx(o)]); } });
    });
    Array.prototype.forEach.call(el.querySelectorAll('.ip-open-btn'), function(b) {
      b.addEventListener('click', function(e) { e.stopPropagation(); openInInbox(items[idx(b)]); });
    });
    Array.prototype.forEach.call(el.querySelectorAll('.ip-del-btn'), function(b) {
      b.addEventListener('click', function(e) { e.stopPropagation(); gmailDelete(items[idx(b)]); });
    });
    Array.prototype.forEach.call(el.querySelectorAll('.ip-read-btn'), function(b) {
      b.addEventListener('click', function(e) { e.stopPropagation(); markRead(items[idx(b)]); });
    });
  }

  function _onDocClick(e) {
    var el = document.getElementById('inbox-preview');
    var badge = document.getElementById('inbox-count-badge');
    if (!el) return;
    if (el.contains(e.target) || (badge && badge.contains(e.target))) return;
    closePreview();
  }
  var _primed = false;
  // ── Popover, or the whole surface ───────────────────────────────────────────────────────────
  // In the header this panel is a dropdown: it opens on hover, closes on an outside click, and
  // closes itself when the count falls to zero because a popover hanging open over nothing is
  // just in the way.
  //
  // The browser extension's toolbar popup renders this SAME panel as its content, where every one
  // of those is wrong — and the last one is a race that empties the popup, because the count is
  // zero until the first answer lands. So a pinned panel is one that never closes itself, and
  // pin() is how a surface that IS this panel says so.
  var _pinned = false;
  /* What the surface puts at the right of the heading bar, rebuilt on every paint because the
     panel is redrawn whole. A factory rather than a node for that reason: a node handed in once
     would be adopted by the first paint and thrown away by the second. */
  var _headAction = null;
  function openPreview() {
    var el = document.getElementById('inbox-preview');
    if (!el) return;
    _previewOpen = true;
    renderPreview();
    el.classList.add('open');
    // Ask once on the first open; after that the poll keeps the answer current, so repeated hovers
    // don't re-ask.
    if (!_primed) { _primed = true; checkNew(); }
    setTimeout(function() { document.addEventListener('click', _onDocClick); }, 0);
  }
  function closePreview() {
    if (_pinned) return;
    var el = document.getElementById('inbox-preview');
    if (el) el.classList.remove('open');
    _previewOpen = false;
    document.removeEventListener('click', _onDocClick);
  }
  // Open this panel and keep it open. For a surface where the panel is the content rather than a
  // dropdown over it; see the note on _pinned above.
  function pinPreview() {
    _pinned = true;
    if (!_previewOpen) openPreview();
    else renderPreview();
  }

  function togglePreview(e) {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (_previewOpen) closePreview(); else openPreview();
    return false;
  }

  /**
   * Hand the server a baseline this browser was still holding.
   *
   * Before 2026.09.12.03 "last opened the Inbox" lived in localStorage, so a reader who has not
   * opened the Inbox since then has a baseline here and none on the server — and mail and Chat
   * count 0 until they next do. The server says so (`hasBaseline: false`); this sends the old
   * value once, removes it, and asks again. The server only ever moves a baseline forward.
   */
  var _handedOver = false;
  function handOverBaseline() {
    if (_handedOver) return;
    _handedOver = true;
    var ts = 0;
    try { ts = parseInt(localStorage.getItem(INBOX_LAST_TS_KEY) || '0', 10) || 0; } catch (_) { /* why-safe: nothing readable to hand over */ }
    if (!ts) return;
    postJson(INBOX_SEEN_URL, { ts: ts }).then(function (d) {
      if (!d || d.ok === false) return;
      try { localStorage.removeItem(INBOX_LAST_TS_KEY); } catch (_) { /* why-safe: the server has it now */ }
      checkNew();
    });
  }

  /**
   * Tell anything listening on this page that the count moved.
   *
   * The browser extension's script on this page hears it and has the toolbar re-count, so the
   * icon follows this badge within a second rather than at its next one-minute check.
   */
  function announce() {
    try { window.dispatchEvent(new CustomEvent('portal-inbox-unread')); } catch (_) { /* why-safe: nothing is listening */ }
  }

  // A recount of one source past the server's cache is a real call to that source. Asked after
  // something changed its read state; a burst of Slack messages would otherwise ask once per
  // message per tab, so each source is asked fresh at most once per FRESH_GAP_MS and otherwise
  // falls back to the cached read.
  var FRESH_GAP_MS = 4000;
  var _freshAt = {};

  /**
   * Ask the server for the count and draw what it says.
   *
   * @param {string} [fresh]  an itemType whose read state just changed: recount that one source
   */
  function checkNew(fresh) {
    var url = INBOX_UNREAD_URL;
    if (fresh && typeof fresh === 'string') {
      var now = Date.now();
      if (!_freshAt[fresh] || now - _freshAt[fresh] >= FRESH_GAP_MS) {
        _freshAt[fresh] = now;
        url += '?fresh=' + encodeURIComponent(fresh);
      }
    }
    // Read BEFORE the request goes out, compared when it comes back — see _mutations above.
    var seq = _mutations;
    return fetch(url).then(function (r) {
      // A failure is "try later", never "none": the badge keeps the last answer it had.
      if (r.ok === false) throw new Error('inbox_unread_' + r.status);
      return r.json();
    }).then(function (d) {
      if (!d || d.ok === false) { schedule(POLL_BASE_MS); return; }
      var wait = count(d.nextPollMs) || POLL_BASE_MS;
      _backoff = d.backoff === true;
      _nextAt = Date.now() + wait;
      schedule(wait);
      if (seq !== _mutations) return;
      var before = _total;
      applyAnswer(d);
      updateBadge();
      if (_previewOpen) renderPreview();
      if (_total !== before) announce();
      if (d.hasBaseline === false) handOverBaseline();
    }).catch(function () {
      // why-safe: the badge keeps its last answer, and asks again after the ordinary wait.
      schedule(POLL_BASE_MS);
    });
  }

  // Another tab changed Slack read state (the Inbox broadcasts after its flag write commits), or
  // another tab's stream saw a new message. Either way the answer is the server's: ask again. The
  // count some of these messages carry is ignored on purpose — it is a second way of counting.
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      var _bc = new BroadcastChannel('portal_inbox');
      _bc.onmessage = function(e) {
        if (!e.data) return;
        if (e.data.type === 'slack_unread_update' || e.data.type === 'slack_new_message') nudge('slack');
      };
    } catch(_) {}
  }

  // The same, from this tab (slack-utils, after a read-state write commits).
  window.addEventListener('portal_slack_unread_local', function() { nudge('slack'); });

  // Back to this tab after reading somewhere else (the extension's popup, another window): ask.
  // Cheap, because the server answers every surface from one cached computation.
  window.addEventListener('focus', function() { nudge(); });

  // Public API for chatheads' SSE handler and the browser extension's popup.
  window.PortalInbox = {
    // refresh(itemType?) — ask again; name a source whose read state just changed to recount it.
    // A nudge: a reader the server has backed off waits for their next scheduled ask.
    refresh: nudge,
    togglePreview: togglePreview,
    // For a surface where this panel is the content rather than a dropdown over it — the browser
    // extension's toolbar popup. See the note on _pinned.
    pinPreview: pinPreview,
    // …and where "open this" should go, for a surface that is not a page. See _go.
    setOpener: function (fn) { _opener = typeof fn === 'function' ? fn : null; },
    // …and what sits at the right of the heading bar. Takes a factory returning a PortalH node.
    setHeadAction: function (fn) { _headAction = typeof fn === 'function' ? fn : null; }
  };

  // Hover-to-open the preview on the Inbox nav item (desktop affordance). A short
  // close delay lets the pointer travel from the link into the panel. focusin/focusout
  // mirror it for the keyboard: the badge used to be a focusable control of its own, and
  // dropping that (it was a button nested inside a link) would otherwise have left the
  // preview mouse-only.
  (function wireHover() {
    var link = document.getElementById('inbox-nav-link');
    if (!link) return;
    var li = (link.closest && link.closest('.nav-item')) || link.parentElement;
    if (!li) return;
    var hoverTimer = null;
    function open() {
      if (hoverTimer) { clearTimeout(hoverTimer); hoverTimer = null; }
      // In the header's menu panel (core.js initSiteMenu) the Inbox link is a plain link: a hover
      // dropdown has nowhere to go inside a panel.
      var header = li.closest && li.closest('.site-header');
      if (header && header.getAttribute('data-nav') === 'compact') return;
      if (total() > 0 && !_previewOpen) openPreview();
    }
    function scheduleClose(delay) {
      if (hoverTimer) clearTimeout(hoverTimer);
      hoverTimer = setTimeout(function() { if (_previewOpen) closePreview(); }, delay);
    }
    li.addEventListener('mouseenter', open);
    li.addEventListener('mouseleave', function() { scheduleClose(220); });
    li.addEventListener('focusin', open);
    li.addEventListener('focusout', function(e) {
      // Tabbing between the link and the panel keeps focus inside; only close on real exit.
      if (e.relatedTarget && li.contains(e.relatedTarget)) return;
      scheduleClose(0);
    });
  })();

  // Paint what the badge was showing before this page load, BEFORE any fetch: that is what carries
  // the indicator across a refresh or a navigation. The poll below replaces it with the server's
  // answer — the cache decides what shows in the meantime, never what the count actually is.
  loadBadgeState();
  updateBadge();

  // The first ask: late enough to stay out of the way of the page's own requests, soon enough to
  // be honest — unless the server has backed this reader off, in which case it keeps the time it
  // gave. Every later ask is scheduled by the answer before it (checkNew → schedule).
  schedule((_backoff && _nextAt > Date.now()) ? _nextAt - Date.now() : 2500);
})();
